import React from "react";
import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";
import AirIcon from "@mui/icons-material/Air";

/**
 * TURBULENCE LEGEND — SLIM CONTINUOUS GRADIENT (HORIZONTAL)
 * Inspirada en la leyenda heatmap de los CNNs. Las gamas de color derivan del
 * `color_text` del pipeline de turbulence (escala EDR, 0.0 → 0.03).
 *
 * ponytail: un solo array STOPS = fuente de verdad para gradiente + etiquetas.
 * Los EDR son los umbrales de cada gama en el color_text (calibración): ajústalos
 * ahí y aquí si el pipeline cambia la rampa.
 */
const STOPS = [
  { label: "Light",           color: "#66caff", edr: 0.0045 }, // 120,200,255 → 60,220,255
  { label: "Light-Moderate",  color: "#50ffa0", edr: 0.0080 }, // 80,255,160
  { label: "Moderate",        color: "#eaf03c", edr: 0.0100 }, // 220,255,80 → 255,235,60
  { label: "Moderate-Severe", color: "#ffaa28", edr: 0.0160 }, // 255,170,40
  { label: "Severe",          color: "#ff5a19", edr: 0.0200 }, // 255,90,25
  { label: "Extreme",         color: "#c81414", edr: 0.0250 }, // 200,20,20 → 120,0,0
];
const END_COLOR = "#780000"; // 120,0,0 tope de la rampa (0.03)

// Etiquetas visibles: 4 referencias simétricas bajo la barra (la rampa conserva las 6 gamas).
const LABELS = ["Light", "Moderate", "Severe", "Extreme"];

// Gradiente con cada color centrado en su gama (1/6 del ancho) → alinea con la etiqueta.
const GRADIENT_HORIZONTAL = `linear-gradient(90deg, ${
  STOPS.map((s, i) => `${s.color} ${(((i + 0.5) / STOPS.length) * 100).toFixed(2)}%`).join(", ")
}, ${END_COLOR} 100%)`;

const Container = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  pointerEvents: "none",
  gap: "8px",
}));

const Title = styled(Box)(() => ({
  fontSize: "0.75rem",
  fontWeight: 900,
  letterSpacing: ".06em",
  textTransform: "uppercase",
  color: "#1a237e",
  textShadow: "0 1px 2px rgba(255,255,255,0.8)",
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "center",
  gap: "6px",
  whiteSpace: "nowrap",
}));

const LegendWrapper = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  width: "372px",
  maxWidth: "92vw",
}));

const LegendBar = styled(Box)(() => ({
  width: "100%",
  height: "18px",
  background: GRADIENT_HORIZONTAL,
  borderRadius: "10px",
  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
  border: "1px solid rgba(255,255,255,0.8)",
}));

const LabelsRow = styled(Box)(() => ({
  display: "flex",
  flexDirection: "row",
  width: "100%",
  paddingTop: "6px",
}));

const Label = styled(Box)(() => ({
  flex: 1,
  textAlign: "center",
  color: "#333",
  fontWeight: 800,
  fontSize: "0.7rem",
  lineHeight: 1.1,
  textTransform: "uppercase",
  textShadow: "0 0 3px rgba(255,255,255,1)",
}));

export default function RiskSelectorTurbulence() {
  return (
    <Container>
      <Title>
        <AirIcon sx={{ fontSize: 18 }} aria-hidden />
        Turbulence
      </Title>

      <LegendWrapper>
        <LegendBar role="img" aria-label="Turbulence intensity scale, light to extreme" />
        <LabelsRow>
          {LABELS.map((label) => (
            <Label key={label}>{label}</Label>
          ))}
        </LabelsRow>
      </LegendWrapper>
    </Container>
  );
}
